import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { TextField, FormControl, FormLabel, MenuItem, Select, FormHelperText } from "@mui/material";

import { CountryAutocomplete, DateField } from "../../../../../../../shared/Form";

import { playerPositionsList } from "../../../../../../User/enums/playerPositions";

import { jobModeList } from "../../../../../enum/jobEnums";
import { CreateTalentJob } from "../../../../../types/createJobSchema";
import CreateJobAccordion from "../../../../../components/CreateJobAccordion";

const BasicDetails = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage.jobDetails" });
  const { t: tPosition } = useTranslation("common", { keyPrefix: "user.playerPosition" });
  const { t: tJobMode } = useTranslation("goHire", { keyPrefix: "enum.jobMode" });
  const positionList = playerPositionsList.map((position) => ({
    value: position,
    label: tPosition(position),
  }));

  const jobModeOptions = jobModeList.map((mode) => ({
    value: mode,
    label: tJobMode(mode),
  }));

  const {
    control,
    formState: { errors },
  } = useFormContext<CreateTalentJob>();

  const hasError = Boolean(
    errors.jobName ||
      errors.primaryPosition ||
      errors.secondaryPosition ||
      errors.jobMode ||
      errors.jobLocation ||
      errors.jobDescription ||
      errors.applyEnds
  );

  return (
    <CreateJobAccordion title={t("basicDetails")} error={hasError}>
      <Controller
        name="jobName"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("jobName")}</FormLabel>
            <TextField
              {...field}
              fullWidth
              placeholder={t("enterJobName")}
              error={!!error}
              helperText={error?.message && t(error.message)}
            />
          </FormControl>
        )}
      />
      <Controller
        name="primaryPosition"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("talentPositionPrimary")}</FormLabel>
            <Select {...field} fullWidth error={!!error}>
              {positionList.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        name="secondaryPosition"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("talentPositionSecondary")}</FormLabel>
            <Select {...field} fullWidth error={!!error}>
              {positionList.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="jobMode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("jobMode")}</FormLabel>
            <Select {...field} fullWidth error={!!error}>
              {jobModeOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="jobLocation"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("chooseLocations")}</FormLabel>
            <CountryAutocomplete {...field} multiple error={!!error} />
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="jobDescription"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("jobDescription")}</FormLabel>
            <TextField
              {...field}
              multiline
              rows={6}
              fullWidth
              placeholder={t("jobDescriptionExample")}
              error={!!error}
            />
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="applyEnds"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("applyEnds")}</FormLabel>
            <DateField placeholder={t("selectEndDate")} {...field} disablePast />
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />
    </CreateJobAccordion>
  );
};

export default BasicDetails;
